import type { CloudSaveMeta } from "../emulator/cloudSaves";
import { isEjsSystem } from "../emulator/ejs";
import { romBaseName, stateSlotOf } from "../emulator/saves";
import type { CloudRom } from "./cloudLibrary";
import type { StoredFile } from "./idbfs";
import { SYSTEM_ORDER, systemOf, type LocalGame, type System } from "./localLibrary";
import { playKey, type GamePlay } from "./playtime";

// The library page's view of one game: wherever it lives (this browser,
// the cloud library), its play time, its in-game save and its state slots,
// joined from all of them. Games pair with saves by base name, as the
// emulators do, and with play time by display name, as the API keys it.

export interface LibrarySlot {
  slot: number;
  /** Epoch ms of the newest copy. */
  at: number;
  local: boolean;
  inCloud: boolean;
}

export interface LibraryGame {
  key: string;
  /** The ROM's file name, or a stand-in built from a save or play record. */
  name: string;
  system: System | null;
  /** This browser has the ROM. */
  local: boolean;
  cloud: CloudRom | null;
  play: GamePlay | null;
  /** The in-game save: battery save or N64 cartridge. */
  localSave: StoredFile | null;
  cloudSaveAt: number | null;
  slots: LibrarySlot[];
  /** Epoch ms of the latest play or save, 0 if neither. */
  lastActivity: number;
}

export interface LibrarySources {
  local: LocalGame[];
  cloudRoms: CloudRom[];
  plays: GamePlay[];
  cloudSaves: CloudSaveMeta[];
  /** mGBA's battery saves and states. */
  mgbaSaves: StoredFile[];
  /** EmulatorJS game saves, by the game they belong to. */
  ejsSaves: Map<string, StoredFile>;
  /** EmulatorJS states held in this browser, as (state file name, epoch ms). */
  ejsStates: [string, number][];
}

// A console this build plays (the web and the desktop app play different ones).
const knownSystem = (system: string): System | null =>
  SYSTEM_ORDER.includes(system as System) ? (system as System) : null;

export function buildLibrary(sources: LibrarySources): LibraryGame[] {
  const games = new Map<string, LibraryGame>();
  const game = (name: string, system: System | null) => {
    const key = romBaseName(name);
    let entry = games.get(key);
    if (!entry) {
      entry = {
        key,
        name,
        system,
        local: false,
        cloud: null,
        play: null,
        localSave: null,
        cloudSaveAt: null,
        slots: [],
        lastActivity: 0,
      };
      games.set(key, entry);
    }
    entry.system ??= system;
    return entry;
  };
  const touch = (entry: LibraryGame, at: number) => {
    entry.lastActivity = Math.max(entry.lastActivity, at);
  };
  const slot = (entry: LibraryGame, n: number, at: number, where: "local" | "cloud") => {
    let known = entry.slots.find((s) => s.slot === n);
    if (!known) {
      known = { slot: n, at, local: false, inCloud: false };
      entry.slots.push(known);
    }
    known.at = Math.max(known.at, at);
    if (where === "local") known.local = true;
    else known.inCloud = true;
    touch(entry, at);
  };

  // The ROMs first, so their real file names win over stand-ins.
  for (const rom of sources.local) {
    const entry = game(rom.name, rom.system);
    entry.name = rom.name;
    entry.local = true;
  }
  for (const rom of sources.cloudRoms) {
    // A console CloudBoy can't run (DS) stays out rather than being listed
    // somewhere it won't play; "unknown" falls back to the file name.
    const system = rom.system === "unknown" ? systemOf(rom.name) : knownSystem(rom.system);
    if (!system || !SYSTEM_ORDER.includes(system)) continue;
    const entry = game(rom.name, system);
    entry.cloud = rom;
    if (!entry.local) entry.name = rom.name;
    // A local copy filed as a Game Boy game (a .7z picked before systems were
    // remembered) belongs to the console the cloud copy is for; the player
    // moves it to the right emulator when it opens.
    else if (isEjsSystem(system) && !isEjsSystem(entry.system ?? "")) entry.system = system;
  }

  // Play time is keyed by display name, which several file names can share.
  const byPlayKey = new Map<string, LibraryGame>();
  for (const entry of games.values()) byPlayKey.set(playKey(entry.name), entry);
  for (const play of sources.plays) {
    const entry = byPlayKey.get(play.game) ?? game(play.name, knownSystem(play.system));
    entry.play = play;
    touch(entry, Date.parse(play.lastPlayed) || 0);
  }

  for (const file of sources.cloudSaves) {
    const at = Date.parse(file.updatedAt) || 0;
    const entry = game(file.name, null);
    const n = stateSlotOf(file.name);
    if (n === null) {
      entry.cloudSaveAt = Math.max(entry.cloudSaveAt ?? 0, at);
      touch(entry, at);
    } else slot(entry, n, at, "cloud");
  }
  for (const file of sources.mgbaSaves) {
    const n = stateSlotOf(file.name);
    const entry = game(file.name, null);
    if (n !== null) slot(entry, n, file.updatedAt, "local");
    else if (file.name.endsWith(".sav")) {
      entry.localSave = file;
      touch(entry, file.updatedAt);
    }
  }
  for (const [rom, file] of sources.ejsSaves) {
    const entry = game(rom, null);
    entry.localSave = file;
    touch(entry, file.updatedAt);
  }
  for (const [file, at] of sources.ejsStates) {
    const n = stateSlotOf(file);
    if (n !== null) slot(game(file, null), n, at, "local");
  }

  for (const entry of games.values()) {
    entry.system ??= entry.local || entry.cloud ? systemOf(entry.name) : null;
    entry.slots.sort((a, b) => a.slot - b.slot);
  }
  return [...games.values()].sort(
    (a, b) => b.lastActivity - a.lastActivity || a.key.localeCompare(b.key, undefined, { sensitivity: "base" }),
  );
}

export type LibraryFilter = "all" | "here" | "cloud";

export function filterLibrary(
  games: LibraryGame[],
  filter: LibraryFilter,
  search: string,
  system: System | null = null,
): LibraryGame[] {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  return games.filter((g) => {
    if (filter === "here" && !g.local) return false;
    if (filter === "cloud" && !g.cloud) return false;
    if (system && g.system !== system) return false;
    const name = g.name.toLowerCase();
    return words.every((word) => name.includes(word));
  });
}

export type LibrarySort = "recent" | "name" | "console" | "played" | "everyone-recent" | "everyone-played";

/** The orders; the "everyone" ones need the dashboard, so they're for the signed in. */
export const LIBRARY_SORTS: { value: LibrarySort; label: string; everyone?: boolean }[] = [
  { value: "recent", label: "Recent" },
  { value: "played", label: "Most played" },
  { value: "name", label: "By name" },
  { value: "console", label: "By console" },
  { value: "everyone-recent", label: "Everyone's recent", everyone: true },
  { value: "everyone-played", label: "Everyone's most played", everyone: true },
];

/** Every player's figures for a game, by its play key (see playtime.ts). */
export type EveryonePlay = Map<string, { seconds: number; lastPlayed: string }>;

const byName = (a: LibraryGame, b: LibraryGame) => a.key.localeCompare(b.key, undefined, { sensitivity: "base" });

/**
 * The list in the chosen order. "recent" and "played" are your own play and
 * saves; the "everyone" orders use the whole account's figures, where a game
 * nobody has played sorts last by name.
 */
export function sortLibrary(games: LibraryGame[], sort: LibrarySort, everyone: EveryonePlay = new Map()): LibraryGame[] {
  const sorted = [...games];
  const all = (g: LibraryGame) => everyone.get(playKey(g.name));
  switch (sort) {
    case "name":
      return sorted.sort(byName);
    case "console":
      return sorted.sort(
        (a, b) => SYSTEM_ORDER.indexOf(a.system ?? SYSTEM_ORDER[0]) - SYSTEM_ORDER.indexOf(b.system ?? SYSTEM_ORDER[0]) || byName(a, b),
      );
    case "played":
      return sorted.sort((a, b) => (b.play?.seconds ?? 0) - (a.play?.seconds ?? 0) || byName(a, b));
    case "everyone-played":
      return sorted.sort((a, b) => (all(b)?.seconds ?? 0) - (all(a)?.seconds ?? 0) || byName(a, b));
    case "everyone-recent":
      return sorted.sort((a, b) => (all(b)?.lastPlayed ?? "").localeCompare(all(a)?.lastPlayed ?? "") || byName(a, b));
    default:
      return sorted.sort((a, b) => b.lastActivity - a.lastActivity || byName(a, b));
  }
}

/** Playable games (in this browser or the cloud library) per console, in console order. */
export function bySystem(games: LibraryGame[]): [System, LibraryGame[]][] {
  const playable = games.filter((g) => (g.local || g.cloud) && g.system);
  return SYSTEM_ORDER.map((system) => [system, playable.filter((g) => g.system === system)] as [System, LibraryGame[]]).filter(
    ([, list]) => list.length > 0,
  );
}
